/**
 * enginePreProcess_Merge_Slot.ts - Slot processing methods for EnginePreProcess
 * Port of C# EnginePreProcess_Merge_Slot.cs
 * Contains processSlottedTemplate and processSlotContent
 */

import { PreprocessedTemplate, TemplateOperation, TemplateOperationKind, JsonArrayPlaceholder } from '../../model/index.js';
import { JsonMergeUtil } from '../../base/engine/jsonMergeUtil.js';
import { CommonUtil } from '../../common/commonUtil.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { msToNanos } from './enginePreProcess_Helper.js';
import type { EnginePreProcess } from './enginePreProcess.js';
import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import type { SlottedTemplate } from '../../model/slottedTemplate.js';
import type { SlotPlaceholder } from '../../model/slotPlaceholder.js';

export function processSlottedTemplate(this: EnginePreProcess, slottedTemplate: SlottedTemplate, appSite: string, currentTemplateName: string, appView: string | null, parentTemplateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): string {
    const slotTimerStart = performance.now();
    try {
        const nestedOperations: TemplateOperation[] = [];

        const cleanTemplateName = slottedTemplate.name.replace(/^!/, '');

        const newFlowStack = [...(flowStack || []), cleanTemplateName];

        let childTemplate: PreprocessedTemplate | null;
        let childJson: JsonObject | null;
        let resolvedTemplateName: string;
        let resolvedAppSite: string;
        let slotWrappedHtml: string;
        let slotLatePlaceholders: string[];

        if (slottedTemplate.isResolved) {
            childTemplate = slottedTemplate.resolvedTemplate as unknown as PreprocessedTemplate;
            childJson = slottedTemplate.resolvedJson;
            resolvedTemplateName = slottedTemplate.resolvedTemplateName!;
            resolvedAppSite = slottedTemplate.resolvedAppSite!;
            const viewResult = this._loader!.applyViewToTemplate(appSite, currentTemplateName, slottedTemplate.name, appView, newFlowStack, configJson, { template: childTemplate, json: childJson, resolvedTemplateName, resolvedAppSite }, clientMode);
            slotWrappedHtml = viewResult.viewHtml;
            slotLatePlaceholders = viewResult.latePlaceholders;
        } else {
            const startGetTemplate = performance.now();
            const result = this._loader!.getTemplateWithView(appSite, currentTemplateName, slottedTemplate.name, appView, newFlowStack, configJson, clientMode);
            childTemplate = result.template;
            childJson = result.json;
            resolvedTemplateName = result.resolvedTemplateName;
            resolvedAppSite = result.resolvedAppSite;
            slotWrappedHtml = result.viewHtml;
            slotLatePlaceholders = result.latePlaceholders;
            this._getTemplateNanos += msToNanos(performance.now() - startGetTemplate);
            this._getTemplateCount++;
        }

        {
            if (slotWrappedHtml !== childTemplate!.originalContent) {
                const wrappedSlotTemplate = new PreprocessedTemplate();
                wrappedSlotTemplate.originalContent = slotWrappedHtml;
                wrappedSlotTemplate.actualTemplateName = childTemplate!.actualTemplateName;
                wrappedSlotTemplate.placeholders = childTemplate!.placeholders;
                wrappedSlotTemplate.slottedTemplates = childTemplate!.slottedTemplates;
                wrappedSlotTemplate.replacementMappings = childTemplate!.replacementMappings;
                wrappedSlotTemplate.jsonArrayPlaceholders = childTemplate!.jsonArrayPlaceholders;
                childTemplate = wrappedSlotTemplate;
            }
        }

        if (resolvedTemplateName && resolvedTemplateName !== cleanTemplateName && newFlowStack.length > 0) {
            newFlowStack[newFlowStack.length - 1] = resolvedTemplateName;
        }

        if (!childTemplate) {
            Logger.flow(`ProcessSlottedTemplate: slot=${slottedTemplate.name} resolved=null`, "PreProcess.SlotExpansion");
            return slottedTemplate.fullMatch;
        }

        const replacedWith = `${resolvedAppSite}_${resolvedTemplateName || slottedTemplate.name}`;
        Logger.flow(`ProcessSlottedTemplate: slot=${slottedTemplate.name} resolved=${resolvedTemplateName || ''} slotCount=${slottedTemplate.slots ? slottedTemplate.slots.length : 0} childSlots=${childTemplate.slottedTemplates.length} childPlaceholders=${childTemplate.placeholders.length}`, "PreProcess.SlotExpansion");

        const isSlotSkipMarked = slottedTemplate.name.startsWith('!');
        const isSlotSkippedOrMissing = resolvedTemplateName === this._loader!.defaultSkipTemplateName || resolvedTemplateName === this._loader!.defaultReplaceTemplateName;

        if (genComponentHtml) {
            componentHtml.set(slottedTemplate.name, JsonValue.string(''));
        }

        let childContent = childTemplate.originalContent;

        if (!childContent) {
            const opType = resolvedTemplateName === this._loader!.defaultSkipTemplateName ? TemplateOperationKind.SlotSkip : TemplateOperationKind.SlotMissing;
            const cleanName = slottedTemplate.name.replace(/^!/, '');
            const targetMarker = slottedTemplate.name.startsWith('!')
                ? `{{!#${cleanName}}}`
                : `{{#${cleanName}}}`;
            const operation = new TemplateOperation();
            operation.templateName = parentTemplateKey;
            operation.kind = opType;
            operation.target = targetMarker;
            operation.replacedWith = replacedWith;
            operation.operations = nestedOperations;
            operations.push(operation);
            return childContent || '';
        }

        if (!isSlotSkipMarked && !isSlotSkippedOrMissing) {
            const jsonToMerge = childJson || new JsonObject();
            const defaultHtmlForMerge = resolvedTemplateName === this._loader!.defaultSkipTemplateName
                ? this._loader!.defaultSkipHtml
                : this._loader!.defaultReplaceHtml;

            const slotArrayMetadata: JsonArrayPlaceholder[] | null = (childTemplate.jsonArrayPlaceholders && childTemplate.jsonArrayPlaceholders.length > 0)
                ? childTemplate.jsonArrayPlaceholders : null;
            const slotJsonKeyMetadata = (childTemplate.jsonKeyPlaceholders && childTemplate.jsonKeyPlaceholders.length > 0)
                ? childTemplate.jsonKeyPlaceholders : null;
            const startJsonMerge = performance.now();
            childContent = JsonMergeUtil.mergeTemplateWithJson(
                childContent,
                jsonToMerge,
                replacedWith,
                resolvedTemplateName || slottedTemplate.name,
                nestedOperations,
                this._loader!.maxArrayOperationItems,
                defaultHtmlForMerge,
                this._loader!.defaultSkipJson,
                slotArrayMetadata,
                slotJsonKeyMetadata,
                contextJson,
                configJson
            );
            this._jsonMergeNanos += msToNanos(performance.now() - startJsonMerge);
            this._jsonMergeCount++;

            for (const slot of slottedTemplate.slots || []) {
                if (!childContent.includes(slot.slotKey)) {
                    continue;
                }

                Logger.flow(`ProcessSlottedTemplate: slotKey=${slot.slotKey} nestedSlots=${slot.nestedSlottedTemplates ? slot.nestedSlottedTemplates.length : 0} nestedPlaceholders=${slot.nestedPlaceholders ? slot.nestedPlaceholders.length : 0}`, "PreProcess.SlotExpansion");
                const slotContentOperations: TemplateOperation[] = [];
                const processedSlotContent = this._processSlotContent(
                    slot,
                    appSite,
                    currentTemplateName,
                    appView,
                    replacedWith,
                    slotContentOperations,
                    recursionDepth + 1,
                    componentHtml,
                    genComponentHtml,
                    contextJson,
                    newFlowStack,
                    configJson,
                    clientMode
                );

                childContent = childContent.replace(slot.slotKey, processedSlotContent);

                const slotName = slot.slotKey.replace('{{$', '').replace('}}', '');
                const slotOperation = new TemplateOperation();
                slotOperation.templateName = replacedWith;
                slotOperation.kind = TemplateOperationKind.SlotMerge;
                slotOperation.target = slot.slotKey;
                slotOperation.replacedWith = slotName;
                slotOperation.operations = slotContentOperations;
                nestedOperations.push(slotOperation);
            }

            childContent = CommonUtil.removeRemainingSlotPlaceholders(childContent);

            childContent = this._processTemplateContent(
                childContent,
                childTemplate,
                appSite,
                cleanTemplateName,
                appView,
                replacedWith,
                nestedOperations,
                recursionDepth + 1,
                componentHtml,
                genComponentHtml,
                contextJson,
                newFlowStack,
                configJson,
                slotLatePlaceholders,
                clientMode
            );
        }

        if (genComponentHtml) {
            componentHtml.set(slottedTemplate.name, JsonValue.string(childContent));
        }

        let finalOpType: TemplateOperationKind = TemplateOperationKind.SlotReplace;
        if (resolvedTemplateName === this._loader!.defaultSkipTemplateName) {
            finalOpType = TemplateOperationKind.SlotSkip;
        } else if (resolvedTemplateName === this._loader!.defaultReplaceTemplateName) {
            finalOpType = TemplateOperationKind.SlotMissing;
        }

        const cleanNameFinal = slottedTemplate.name.replace(/^!/, '');
        const targetMarkerFinal = slottedTemplate.name.startsWith('!')
            ? `{{!#${cleanNameFinal}}}`
            : `{{#${cleanNameFinal}}}`;
        const operation = new TemplateOperation();
        operation.templateName = parentTemplateKey;
        operation.kind = finalOpType;
        operation.target = targetMarkerFinal;
        operation.replacedWith = replacedWith;
        operation.operations = nestedOperations;
        operations.push(operation);

        return childContent;
    } finally {
        this._slotProcessNanos += msToNanos(performance.now() - slotTimerStart);
        this._slotProcessCount++;
    }
}

export function processSlotContent(this: EnginePreProcess, slot: SlotPlaceholder, appSite: string, currentTemplateName: string, appView: string | null, contextTemplateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): string {
    let result = slot.content;

    for (const nestedSlot of slot.nestedSlottedTemplates || []) {
        if (!result.includes(nestedSlot.fullMatch)) {
            continue;
        }

        Logger.flow(`ProcessSlotContent: nestedSlot=${nestedSlot.name}`, "PreProcess.SlotExpansion");
        const processedNestedSlot = this._processSlottedTemplate(
            nestedSlot,
            appSite,
            currentTemplateName,
            appView,
            contextTemplateKey,
            operations,
            recursionDepth,
            componentHtml,
            genComponentHtml,
            contextJson,
            flowStack,
            configJson,
            clientMode
        );

        result = result.replaceAll(nestedSlot.fullMatch, processedNestedSlot);
    }

    for (const nestedPlaceholder of slot.nestedPlaceholders || []) {
        if (!result.includes(nestedPlaceholder.fullMatch)) {
            continue;
        }

        Logger.flow(`ProcessSlotContent: nestedPlaceholder=${nestedPlaceholder.name}`, "PreProcess.SlotExpansion");
        Logger.flow(`ProcessTemplateContent: placeholder=${nestedPlaceholder.name}`, "PreProcess.TemplateRecursion");
        const processedNested = this._processPlaceholder(
            nestedPlaceholder,
            appSite,
            currentTemplateName,
            appView,
            contextTemplateKey,
            operations,
            recursionDepth,
            componentHtml,
            genComponentHtml,
            contextJson,
            flowStack,
            configJson,
            clientMode
        );

        result = result.replaceAll(nestedPlaceholder.fullMatch, processedNested);
    }

    return result;
}
